import { CONSENT_CATEGORIES, ConsentCategory } from "lib/consent/ConsentTypes";

// ---- OneTrust domain-data shapes (partial — only what we read) -------------

interface OtCookie {
  Name: string;
  Host?: string;
}

interface OtHost {
  HostId: string;
  Cookies?: OtCookie[];
}

interface OtGroup {
  CustomGroupId: string; // e.g. "C0002"
  Cookies?: OtCookie[];
  Hosts?: OtHost[];
}

interface OtDomainData {
  Groups: OtGroup[];
}

interface OptanonGlobal {
  GetDomainData?: () => OtDomainData;
}

// Ecolab-specific fixed domains the legacy always swept, regardless of host.
const ECOLAB_DOMAIN = "ecolab.com";

/**
 * Cookies the legacy script deleted explicitly by name (not always present in
 * GetDomainData). Kept as a safety supplement, keyed by category.
 * localStorage keys are handled separately below.
 */
const SUPPLEMENTAL_COOKIES: Partial<Record<ConsentCategory, string[]>> = {
  performance: ["SC_ANALYTICS_GLOBAL_COOKIE", "ADRUM"],
  // `sc_<id>` is the Sitecore CloudSDK browser identifier written by
  // CdpPageView, which is gated on `targeting`. Verified on QA as absent from
  // OneTrust's cookie inventory entirely, so no inventory matcher can reach it.
  // Registering it in the OneTrust console is the durable fix — this entry only
  // stops a known identifier from outliving a rejection in the meantime.
  // NOTE: Google's `_gcl_au` is intentionally NOT listed here — GA/GTM are
  // exempt from all sweeps by client request (see GOOGLE_EXEMPT below).
  targeting: ["sc_xxxxxxxx"],
};

const SUPPLEMENTAL_LOCAL_STORAGE: Partial<Record<ConsentCategory, string[]>> = {
  performance: ["ADRUM_CLIENTINFO", "ADRUM_XD_AGENT_INFO"],
};

// ---------------------------------------------------------------------------

const EXPIRED = "Max-Age=-99999999";

/** Every host we try to scope a deletion to (current host + its dot-prefixed
 * form, plus the fixed ecolab.com pair), matching the legacy sweep. */
const candidateDomains = (host: string): string[] => {
  const set = new Set<string>([
    host,
    `.${host}`,
    ECOLAB_DOMAIN,
    `.${ECOLAB_DOMAIN}`,
  ]);
  return Array.from(set);
};

/** Every path prefix of the current page, mirroring the legacy pathArray walk
 * (e.g. /a/b/c -> ['/a', '/a/b', '/a/b/c']), plus root. */
const candidatePaths = (pathname: string): string[] => {
  const parts = pathname.split("/").filter(Boolean);
  const paths = ["/"];
  for (let i = 0; i < parts.length; i++) {
    paths.push("/" + parts.slice(0, i + 1).join("/"));
  }
  return paths;
};

/**
 * Expire one cookie across every domain/path/trailing-slash permutation the
 * legacy tried. Brute force by design: you can't read a cookie's exact
 * domain/path back from document.cookie, so you clear every plausible scope.
 */
const expireCookieEverywhere = (
  name: string,
  cookieHost: string | undefined,
  pageHost: string,
  pathname: string,
): void => {
  const domains = candidateDomains(cookieHost || pageHost);
  const paths = candidatePaths(pathname);

  for (const path of paths) {
    for (const withSlash of [true, false]) {
      const p = withSlash ? `${path}/` : path;

      // Domain-scoped deletions.
      for (const domain of domains) {
        document.cookie = `${name}=; ${EXPIRED}; Path=${p};Domain=${domain}`;
      }
      // Host-only (no Domain attr) deletion — the legacy did this too.
      document.cookie = `${name}=; ${EXPIRED}; Path=${p};`;
    }
  }
};

/** Safely read OneTrust's cookie inventory for a specific category id. */
const cookiesForCategory = (categoryId: string): OtCookie[] => {
  const optanon = (window as unknown as { Optanon?: OptanonGlobal }).Optanon;
  const data = optanon?.GetDomainData?.();
  if (!data?.Groups?.length) return [];

  const collected: OtCookie[] = [];

  for (const group of data.Groups) {
    if (group.CustomGroupId !== categoryId) continue;

    // Group-level cookies.
    for (const c of group.Cookies ?? []) collected.push(c);

    // Host-level cookies (third parties registered under this category).
    for (const host of group.Hosts ?? []) {
      for (const c of host.Cookies ?? []) collected.push(c);
    }
  }

  return collected;
};

/**
 * OneTrust registers variable cookie names with placeholder runs rather than
 * globs — e.g. `_ga_xxxxxxxxxx`, `ADRUM_xxxx`, `_hjSession_xxxxxx`,
 * `KRTBCOOKIE_XXXX`, `_utmv#########`. Matching those literally deletes
 * nothing, which is how `_ga_01PR1VRT90` survived a full Reject All.
 *
 * Treat a run of 3+ `x`, `X` or `#` as the placeholder. Three is deliberate:
 * shorter runs appear in real cookie names, longer runs effectively never do.
 */
const PLACEHOLDER_RUN = /[xX#]{3,}/g;

const isPattern = (name: string): boolean => {
  PLACEHOLDER_RUN.lastIndex = 0;
  return PLACEHOLDER_RUN.test(name);
};

const escapeRegExp = (s: string): string =>
  s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** Turn a OneTrust placeholder name into a matcher over real cookie names. */
const patternToRegExp = (name: string): RegExp => {
  const source = name
    .split(PLACEHOLDER_RUN)
    .map(escapeRegExp)
    .join("[A-Za-z0-9_-]+");
  return new RegExp(`^${source}$`);
};

/**
 * Cookies that must never be swept, whatever the inventory says. Deleting
 * OneTrust's own records would erase the very rejection that triggered this
 * sweep, and the next page load would re-grant everything under the opt-out
 * geolocation rule.
 */
const NEVER_DELETE = new Set([
  "OptanonConsent",
  "OptanonAlertBoxClosed",
  "eupubconsent-v2",
]);

/**
 * Google Analytics / Google Tag Manager cookies are exempt from every consent
 * sweep by client request: GTM is ungated and its cookies must be unaffected by
 * any consent change, including Reject All. Matched by name regardless of which
 * OneTrust category claims them — GA appears under C0002 (performance) and C0004
 * (targeting) in this tenant, so a category-agnostic name guard is the only
 * reliable exemption.
 */
const GOOGLE_EXEMPT: RegExp[] = [
  /^_ga(_.+)?$/, // _ga, _ga_<container> (e.g. _ga_01PR1VRT90)
  /^_gid$/,
  /^_gat(_.+)?$/, // _gat, _gat_<tracker>, _gat_gtag_<id>
  /^_gcl_/, // _gcl_au, _gcl_aw, _gcl_dc, _gcl_gb (Google conversion linker)
  /^_dc_gtm_/, // GA request throttling proxied through GTM
  /^__utm[abcvz]$/, // Universal Analytics legacy
  /^AMP_TOKEN$/,
];

const isGoogleExempt = (name: string): boolean =>
  GOOGLE_EXEMPT.some((re) => re.test(name));

/** Cookie names actually present in the browser right now. */
const currentCookieNames = (): string[] =>
  document.cookie
    .split(";")
    .map((c) => c.trim().split("=")[0])
    .filter(Boolean);

const protectedMatchers = (): { exact: Set<string>; patterns: RegExp[] } => {
  const exact = new Set<string>();
  const patterns: RegExp[] = [];
  for (const c of cookiesForCategory(CONSENT_CATEGORIES.strictlyNecessary)) {
    if (isPattern(c.Name)) patterns.push(patternToRegExp(c.Name));
    else exact.add(c.Name);
  }
  return { exact, patterns };
};

type SweepOptions = {
  presentOnly?: boolean;
};

const sweepCategories = (
  categories: ConsentCategory[],
  options: SweepOptions = {},
): void => {
  if (typeof window === "undefined" || categories.length === 0) return;

  const pageHost = window.location.hostname;
  const pathname = window.location.pathname;
  const present = currentCookieNames();
  const presentSet = new Set(present);
  const protectedNames = protectedMatchers();

  const isProtected = (name: string): boolean =>
    protectedNames.exact.has(name) ||
    protectedNames.patterns.some((re) => re.test(name));

  // Dedupe by name+host so we don't re-run the expensive sweep needlessly.
  const seen = new Set<string>();

  const sweep = (name: string, host?: string) => {
    if (NEVER_DELETE.has(name)) return;
    if (isGoogleExempt(name)) return; // GA/GTM are never swept, in any category
    if (isProtected(name)) return;
    if (options.presentOnly && !presentSet.has(name)) return;
    const key = `${name}::${host ?? ""}`;
    if (seen.has(key)) return;
    seen.add(key);
    expireCookieEverywhere(name, host, pageHost, pathname);
  };

  const sweepInventoryEntry = (name: string, host?: string) => {
    if (!isPattern(name)) {
      sweep(name, host);
      return;
    }
    const re = patternToRegExp(name);
    for (const actual of present) {
      if (re.test(actual)) sweep(actual, host);
    }
  };

  for (const category of categories) {
    const categoryId = CONSENT_CATEGORIES[category]; // e.g. "C0002"

    // 1) Authoritative: cookies OneTrust registers under this category.
    for (const cookie of cookiesForCategory(categoryId)) {
      sweepInventoryEntry(cookie.Name, cookie.Host);
    }

    // 2) Supplement: cookies the legacy deleted explicitly by name.
    for (const name of SUPPLEMENTAL_COOKIES[category] ?? []) {
      sweepInventoryEntry(name, pageHost);
    }

    // 3) localStorage keys the legacy removed for this category.
    for (const lsKey of SUPPLEMENTAL_LOCAL_STORAGE[category] ?? []) {
      try {
        window.localStorage.removeItem(lsKey);
      } catch {
        /* storage access can throw in some privacy modes — ignore */
      }
    }
  }
};

export const clearDowngradedCookies = (downgraded: ConsentCategory[]): void =>
  sweepCategories(downgraded);

export const clearDeniedCookies = (denied: ConsentCategory[]): void =>
  sweepCategories(denied, { presentOnly: true });
